/* eslint-disable space-before-function-paren */
import * as Turbo from '@hotwired/turbo'
import { encodeMethodIntoRequestBody } from '@hotwired/turbo-rails/app/javascript/turbo/fetch_requests'

Turbo.start()

// Import Stimulus
import { Application } from '@hotwired/stimulus'
import { definitionsFromContext } from '@hotwired/stimulus-webpack-helpers'

import { createApp, reactive } from 'vue'
import TemplateBuilder from './template_builder/builder'
import TemplateBuilderNew from './template_builder/builder_new.vue'
import ImportList from './template_builder/import_list'

import ToggleVisible from './elements/toggle_visible'
import ToggleCookies from './elements/toggle_cookies'
import DisableHidden from './elements/disable_hidden'
import TurboModal from './elements/turbo_modal'
import FileDropzone from './elements/file_dropzone'
import MenuActive from './elements/menu_active'
import NewMenuActive from './elements/new_menu_active'
import ClipboardCopy from './elements/clipboard_copy'
import DynamicList from './elements/dynamic_list'
import DynamicListV2 from './elements/dynamic_list_v2'
import SortableSubmitters from './elements/sortable_submitters'
import DownloadButton from './elements/download_button'
import ToggleBccRecipients from './elements/toggle_bcc_recipients'
import SetOriginUrl from './elements/set_origin_url'
import SetTimezone from './elements/set_timezone'
import AutoresizeTextarea from './elements/autoresize_textarea'
import SubmittersAutocomplete from './elements/submitter_autocomplete'
import FolderAutocomplete from './elements/folder_autocomplete'
import SignatureForm from './elements/signature_form'
import SubmitForm from './elements/submit_form'
import PromptPassword from './elements/prompt_password'
import EmailsTextarea from './elements/emails_textarea'
import ToggleOnSubmit from './elements/toggle_on_submit'
import SubmitButton from './elements/submit_button'
import OtherActions from './submission_form/other_actions.vue'
import DateTimePicker from './components/date_time_picker.vue'
import DateRangePicker from './components/date_range_picker.vue'
import DateRangePickerV2 from './components/date_range_picker_v2.vue'
import DateRangePickerV3 from './components/date_range_picker_v3.vue'
import NewSubmittersAutocomplete from './components/submission/submitters_autocomplete.vue'
import NewSubmittersAutocompleteV2 from './components/submission/submitters_autocomplete_v2.vue'
import ExpirationSubmissionForm from './components/submission/expiration_submission_form.vue'
import NewExpirationSubmissionForm from './components/submission/new_expiration_submission_form.vue'
import ReminderSubmissionForm from './components/submission/reminder_submission_form.vue'
import NewReminderSubmissionForm from './components/submission/new_reminder_submission_form.vue'
import DropdownDepartment from './components/dropdown_department.vue'
import NewDropdownDepartment from './components/new_dropdown_department.vue'
import PdfComments from './components/submission/pdf_comment_form.vue'
import NewPdfComments from './components/submission/new_pdf_comment_form.vue'
import NewCommentsSidebar from './components/submission/new_comments_sidebar.vue'
import NewBccSubmittersAutoComplete from './components/submission/bcc_submitters_autocomplete.vue'
import NewBccSubmittersAutoCompleteV2 from './components/submission/bcc_submitters_autocomplete_v2.vue'
import NewCcSubmittersAutoComplete from './components/submission/cc_submitters_autocomplete.vue'
import NewCcSubmittersAutoCompleteV2 from './components/submission/cc_submitters_autocomplete_v2.vue'
import RichTextEditor from './components/rich_text_editor.vue'
import LandingPageV2 from './pages/landing-page/index.vue'
import * as TurboInstantClick from './lib/turbo_instant_click'

import './images/preview.png'
import './images/auth_background.png'
import './images/new_auth_background.png'
import './images/auth_background_purple.svg'

TurboInstantClick.start()

document.addEventListener('turbo:before-cache', () => {
  window.flash?.remove()
})

document.addEventListener('keyup', (e) => {
  if (e.code === 'Escape') {
    document.activeElement?.blur()
  }
})

document.addEventListener(
  'turbo:before-fetch-request',
  encodeMethodIntoRequestBody
)
document.addEventListener('turbo:submit-end', async (event) => {
  const resp = event.detail?.formSubmission?.result?.fetchResponse?.response

  if (!resp?.headers?.get('content-disposition')?.includes('attachment')) {
    return
  }

  const url = URL.createObjectURL(await resp.blob())
  const link = document.createElement('a')

  link.href = url
  link.setAttribute(
    'download',
    decodeURIComponent(resp.headers.get('content-disposition').split('"')[1])
  )

  document.body.appendChild(link)

  link.click()

  document.body.removeChild(link)

  URL.revokeObjectURL(url)
})

const safeRegisterElement = (name, element, options = {}) =>
  !window.customElements.get(name) &&
  window.customElements.define(name, element, options)

safeRegisterElement('toggle-visible', ToggleVisible)
safeRegisterElement('disable-hidden', DisableHidden)
safeRegisterElement('turbo-modal', TurboModal)
safeRegisterElement('file-dropzone', FileDropzone)
safeRegisterElement('menu-active', MenuActive)
safeRegisterElement('new-menu-active', NewMenuActive)
safeRegisterElement('clipboard-copy', ClipboardCopy)
safeRegisterElement('dynamic-list', DynamicList)
safeRegisterElement('dynamic-list-v2', DynamicListV2)
safeRegisterElement('sortable-submitters', SortableSubmitters)
safeRegisterElement('download-button', DownloadButton)
safeRegisterElement('toggle-bcc-recipients', ToggleBccRecipients)
safeRegisterElement('set-origin-url', SetOriginUrl)
safeRegisterElement('set-timezone', SetTimezone)
safeRegisterElement('autoresize-textarea', AutoresizeTextarea)
safeRegisterElement('submitters-autocomplete', SubmittersAutocomplete)
safeRegisterElement('folder-autocomplete', FolderAutocomplete)
safeRegisterElement('signature-form', SignatureForm)
safeRegisterElement('submit-form', SubmitForm)
safeRegisterElement('prompt-password', PromptPassword)
safeRegisterElement('emails-textarea', EmailsTextarea)
safeRegisterElement('toggle-cookies', ToggleCookies)
safeRegisterElement('toggle-on-submit', ToggleOnSubmit)
safeRegisterElement('submit-button', SubmitButton)

safeRegisterElement(
  'landing-page-v2',
  class extends HTMLElement {
    connectedCallback() {
      // Prevent double initialization
      if (this.app) {
        return
      }

      this.appElem = document.createElement('div')
      this.app = createApp(LandingPageV2, {
        class: this.getAttribute('class'),
        signupUrl: this.dataset.signupUrl
      })

      this.app.mount(this.appElem)
      this.appendChild(this.appElem)

      // Add a flag to track initialization
      this.setAttribute('data-initialized', 'true')
    }

    disconnectedCallback() {
      if (this.app) {
        this.app.unmount()
        this.app = null
      }
      if (this.appElem) {
        this.appElem.remove()
        this.appElem = null
      }
      this.removeAttribute('data-initialized')
    }
  }
)

safeRegisterElement(
  'template-builder',
  class extends HTMLElement {
    connectedCallback() {
      this.appElem = document.createElement('div')

      this.appElem.classList.add('md:h-screen')

      this.app = createApp(TemplateBuilder, {
        template: reactive(JSON.parse(this.dataset.template)),
        backgroundColor: '#D8D7F4',
        withPhone: this.dataset.withPhone === 'true',
        withLogo: this.dataset.withLogo !== 'false',
        editable: this.dataset.editable !== 'false',
        authenticityToken: document.querySelector('meta[name="csrf-token"]')
          ?.content,
        withPayment: this.dataset.withPayment === 'true',
        isPaymentConnected: this.dataset.isPaymentConnected === 'true',
        withFormula: this.dataset.withFormula === 'true',
        withConditions: this.dataset.withConditions === 'true',
        currencies: (this.dataset.currencies || '').split(',').filter(Boolean),
        acceptFileTypes: this.dataset.acceptFileTypes
      })

      this.app.mount(this.appElem)

      this.appendChild(this.appElem)
    }

    disconnectedCallback() {
      this.app?.unmount()
      this.appElem?.remove()
    }
  }
)

safeRegisterElement(
  'template-builder-new',
  class extends HTMLElement {
    connectedCallback() {
      this.appElem = document.createElement('div')

      this.appElem.classList.add('md:h-screen')

      this.app = createApp(TemplateBuilderNew, {
        template: reactive(JSON.parse(this.dataset.template)),
        backgroundColor: '#D8D7F4',
        withPhone: this.dataset.withPhone === 'true',
        withLogo: this.dataset.withLogo !== 'false',
        editable: this.dataset.editable !== 'false',
        authenticityToken: document.querySelector('meta[name="csrf-token"]')
          ?.content,
        withPayment: this.dataset.withPayment === 'true',
        isPaymentConnected: this.dataset.isPaymentConnected === 'true',
        withFormula: this.dataset.withFormula === 'true',
        withConditions: this.dataset.withConditions === 'true',
        currencies: (this.dataset.currencies || '').split(',').filter(Boolean),
        acceptFileTypes: this.dataset.acceptFileTypes
      })

      this.app.mount(this.appElem)

      this.appendChild(this.appElem)
    }

    disconnectedCallback() {
      this.app?.unmount()
      this.appElem?.remove()
    }
  }
)

safeRegisterElement(
  'import-list',
  class extends HTMLElement {
    connectedCallback() {
      this.appElem = document.createElement('div')

      this.app = createApp(ImportList, {
        template: JSON.parse(this.dataset.template),
        multitenant: this.dataset.multitenant === 'true',
        authenticityToken: document.querySelector('meta[name="csrf-token"]')
          ?.content
      })

      this.app.mount(this.appElem)

      this.appendChild(this.appElem)
    }

    disconnectedCallback() {
      this.app?.unmount()
      this.appElem?.remove()
    }
  }
)

safeRegisterElement(
  'select-all-checkbox',
  class extends HTMLElement {
    connectedCallback() {
      this.selectAllCheckbox = this.querySelector('#select-all')
      this.itemCheckboxes = document.querySelectorAll(
        'input[name="template_ids[]"]'
      )
      this.transferBtn = document.getElementById('transfer-btn')

      this.selectAllCheckbox.addEventListener('change', () => {
        this.toggleCheckboxes(this.selectAllCheckbox.checked)
        this.updateTransferButton()
      })

      this.itemCheckboxes.forEach((checkbox) => {
        checkbox.addEventListener('change', () => {
          this.updateSelectAllCheckbox()
          this.updateTransferButton()
        })
      })

      this.updateTransferButton()
    }

    toggleCheckboxes(isChecked) {
      this.itemCheckboxes.forEach((checkbox) => {
        checkbox.checked = isChecked
      })
    }

    updateSelectAllCheckbox() {
      const allChecked = Array.from(this.itemCheckboxes).every(
        (checkbox) => checkbox.checked
      )
      const someChecked = Array.from(this.itemCheckboxes).some(
        (checkbox) => checkbox.checked
      )

      this.selectAllCheckbox.checked = allChecked
      this.selectAllCheckbox.indeterminate = !allChecked && someChecked
    }

    updateTransferButton() {
      if (!this.transferBtn) return
      const anyChecked = Array.from(this.itemCheckboxes).some(cb => cb.checked)
      this.transferBtn.disabled = !anyChecked
    }
  }
)

safeRegisterElement(
  'date-time-picker',
  class extends HTMLElement {
    connectedCallback() {
      this.appElem = document.createElement('div')
      this.app = createApp(DateTimePicker, {
        title: this.getAttribute('title'),
        inputName: this.getAttribute('input-name')
      })

      this.app.mount(this.appElem)

      this.appendChild(this.appElem)
    }

    disconnectedCallback() {
      this.app?.unmount()
      this.appElem?.remove()
    }
  }
)

safeRegisterElement(
  'date-range-picker',
  class extends HTMLElement {
    connectedCallback() {
      this.appElem = document.createElement('div')
      this.app = createApp(DateRangePicker)

      this.app.mount(this.appElem)

      this.appendChild(this.appElem)
    }

    disconnectedCallback() {
      this.app?.unmount()
      this.appElem?.remove()
    }
  }
)

safeRegisterElement(
  'date-range-picker-v2',
  class extends HTMLElement {
    connectedCallback() {
      this.appElem = document.createElement('div')
      this.app = createApp(DateRangePickerV2)

      this.app.mount(this.appElem)

      this.appendChild(this.appElem)
    }

    disconnectedCallback() {
      this.app?.unmount()
      this.appElem?.remove()
    }
  }
)

safeRegisterElement(
  'date-range-picker-v3',
  class extends HTMLElement {
    connectedCallback() {
      if (this.app) return
      this.appElem = document.createElement('div')
      this.app = createApp(DateRangePickerV3, {
        startName:    this.getAttribute('start-name')    || 'started_at',
        endName:      this.getAttribute('end-name')      || 'ended_at',
        initialStart: this.getAttribute('initial-start') || '',
        initialEnd:   this.getAttribute('initial-end')   || '',
        formId:       this.getAttribute('form-id')       || ''
      })
      this.app.mount(this.appElem)
      this.appendChild(this.appElem)
    }

    disconnectedCallback() {
      this.app?.unmount()
      this.app = null
      this.appElem?.remove()
      this.appElem = null
    }
  }
)

safeRegisterElement(
  'new-submitter-autocomplete',
  class extends HTMLElement {
    connectedCallback() {
      this.appElem = document.createElement('div')
      this.app = createApp(NewSubmittersAutocomplete, {
        title: this.getAttribute('title'),
        inputNameName: this.getAttribute('input-name-name'),
        inputNameEmail: this.getAttribute('input-name-email'),
        email: this.getAttribute('email')
      })

      this.app.mount(this.appElem)

      this.appendChild(this.appElem)
    }

    disconnectedCallback() {
      this.app?.unmount()
      this.appElem?.remove()
    }
  }
)

safeRegisterElement(
  'new-submitter-autocomplete-v2',
  class extends HTMLElement {
    connectedCallback() {
      this.appElem = document.createElement('div')
      this.app = createApp(NewSubmittersAutocompleteV2, {
        title: this.getAttribute('title'),
        inputNameName: this.getAttribute('input-name-name'),
        inputNameEmail: this.getAttribute('input-name-email'),
        email: this.getAttribute('email')
      })

      this.app.mount(this.appElem)

      this.appendChild(this.appElem)
    }

    disconnectedCallback() {
      this.app?.unmount()
      this.appElem?.remove()
    }
  }
)

safeRegisterElement(
  'new-bcc-submitter-autocomplete',
  class extends HTMLElement {
    connectedCallback() {
      this.appElem = document.createElement('div')
      this.app = createApp(NewBccSubmittersAutoComplete, {
        title: this.getAttribute('title'),
        inputNameName: this.getAttribute('input-name-name'),
        inputNameEmail: this.getAttribute('input-name-email'),
        email: this.getAttribute('email')
      })

      this.app.mount(this.appElem)

      this.appendChild(this.appElem)
    }

    disconnectedCallback() {
      this.app?.unmount()
      this.appElem?.remove()
    }
  }
)

safeRegisterElement(
  'new-cc-submitter-autocomplete',
  class extends HTMLElement {
    connectedCallback() {
      this.appElem = document.createElement('div')
      this.app = createApp(NewCcSubmittersAutoComplete, {
        title: this.getAttribute('title'),
        inputNameName: this.getAttribute('input-name-name'),
        inputNameEmail: this.getAttribute('input-name-email'),
        email: this.getAttribute('email')
      })

      this.app.mount(this.appElem)

      this.appendChild(this.appElem)
    }

    disconnectedCallback() {
      this.app?.unmount()
      this.appElem?.remove()
    }
  }
)

safeRegisterElement(
  'new-bcc-submitter-autocomplete',
  class extends HTMLElement {
    connectedCallback() {
      this.appElem = document.createElement('div')
      this.app = createApp(NewBccSubmittersAutoComplete, {
        title: this.getAttribute('title'),
        inputNameName: this.getAttribute('input-name-name'),
        inputNameEmail: this.getAttribute('input-name-email'),
        email: this.getAttribute('email')
      })

      this.app.mount(this.appElem)

      this.appendChild(this.appElem)
    }

    disconnectedCallback() {
      this.app?.unmount()
      this.appElem?.remove()
    }
  }
)

safeRegisterElement(
  'new-cc-submitter-autocomplete',
  class extends HTMLElement {
    connectedCallback() {
      this.appElem = document.createElement('div')
      this.app = createApp(NewCcSubmittersAutoComplete, {
        title: this.getAttribute('title'),
        inputNameName: this.getAttribute('input-name-name'),
        inputNameEmail: this.getAttribute('input-name-email'),
        email: this.getAttribute('email')
      })

      this.app.mount(this.appElem)

      this.appendChild(this.appElem)
    }

    disconnectedCallback() {
      this.app?.unmount()
      this.appElem?.remove()
    }
  }
)

safeRegisterElement(
  'new-bcc-submitter-autocomplete-v2',
  class extends HTMLElement {
    connectedCallback() {
      this.appElem = document.createElement('div')
      this.app = createApp(NewBccSubmittersAutoCompleteV2, {
        title: this.getAttribute('title'),
        inputNameName: this.getAttribute('input-name-name'),
        inputNameEmail: this.getAttribute('input-name-email'),
        email: this.getAttribute('email')
      })

      this.app.mount(this.appElem)

      this.appendChild(this.appElem)
    }

    disconnectedCallback() {
      this.app?.unmount()
      this.appElem?.remove()
    }
  }
)

safeRegisterElement(
  'new-cc-submitter-autocomplete-v2',
  class extends HTMLElement {
    connectedCallback() {
      this.appElem = document.createElement('div')
      this.app = createApp(NewCcSubmittersAutoCompleteV2, {
        title: this.getAttribute('title'),
        inputNameName: this.getAttribute('input-name-name'),
        inputNameEmail: this.getAttribute('input-name-email'),
        email: this.getAttribute('email')
      })

      this.app.mount(this.appElem)

      this.appendChild(this.appElem)
    }

    disconnectedCallback() {
      this.app?.unmount()
      this.appElem?.remove()
    }
  }
)

safeRegisterElement(
  'expiration-submission-form',
  class extends HTMLElement {
    connectedCallback() {
      this.appElem = document.createElement('div')
      this.app = createApp(ExpirationSubmissionForm, {
        class: this.getAttribute('class')
      })

      this.app.mount(this.appElem)

      this.appendChild(this.appElem)
    }

    disconnectedCallback() {
      this.app?.unmount()
      this.appElem?.remove()
    }
  }
)

safeRegisterElement(
  'new-expiration-submission-form',
  class extends HTMLElement {
    connectedCallback() {
      this.appElem = document.createElement('div')
      this.app = createApp(NewExpirationSubmissionForm, {
        class: this.getAttribute('class')
      })

      this.app.mount(this.appElem)

      this.appendChild(this.appElem)
    }

    disconnectedCallback() {
      this.app?.unmount()
      this.appElem?.remove()
    }
  }
)

safeRegisterElement(
  'reminder-submission-form',
  class extends HTMLElement {
    connectedCallback() {
      this.appElem = document.createElement('div')
      this.app = createApp(ReminderSubmissionForm, {
        class: this.getAttribute('class')
      })

      this.app.mount(this.appElem)

      this.appendChild(this.appElem)
    }

    disconnectedCallback() {
      this.app?.unmount()
      this.appElem?.remove()
    }
  }
)

safeRegisterElement(
  'new-reminder-submission-form',
  class extends HTMLElement {
    connectedCallback() {
      this.appElem = document.createElement('div')
      this.app = createApp(NewReminderSubmissionForm, {
        class: this.getAttribute('class')
      })

      this.app.mount(this.appElem)

      this.appendChild(this.appElem)
    }

    disconnectedCallback() {
      this.app?.unmount()
      this.appElem?.remove()
    }
  }
)

safeRegisterElement(
  'dropdown-department',
  class extends HTMLElement {
    connectedCallback() {
      this.appElem = document.createElement('div')
      this.app = createApp(DropdownDepartment, {
        class: this.getAttribute('class'),
        departments: this.getAttribute('departments'),
        accountId: this.getAttribute('account-id'),
        value: this.getAttribute('value'),
        errorMessage: JSON.parse(this.getAttribute('error-message')),
        adminRole: this.getAttribute('admin-role')
      })

      this.app.mount(this.appElem)

      this.appendChild(this.appElem)
    }

    disconnectedCallback() {
      this.app?.unmount()
      this.appElem?.remove()
    }
  }
)

safeRegisterElement(
  'new-dropdown-department',
  class extends HTMLElement {
    connectedCallback() {
      this.appElem = document.createElement('div')
      this.app = createApp(NewDropdownDepartment, {
        class: this.getAttribute('class'),
        departments: this.getAttribute('departments'),
        accountId: this.getAttribute('account-id'),
        value: this.getAttribute('value'),
        errorMessage: JSON.parse(this.getAttribute('error-message')),
        adminRole: this.getAttribute('admin-role')
      })

      this.app.mount(this.appElem)

      this.appendChild(this.appElem)
    }

    disconnectedCallback() {
      this.app?.unmount()
      this.appElem?.remove()
    }
  }
)

safeRegisterElement(
  'other-actions',
  class extends HTMLElement {
    connectedCallback() {
      this.appElem = document.createElement('div')
      this.app = createApp(OtherActions, {
        class: this.getAttribute('class')
      })

      this.app.mount(this.appElem)

      this.appendChild(this.appElem)
    }

    disconnectedCallback() {
      this.app?.unmount()
      this.appElem?.remove()
    }
  }
)

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-vue-comment]').forEach((el) => {
    const documentUuid = el.getAttribute('data-document-uuid')
    const submitters = el.getAttribute('data-submitters')
    const pageIndex = parseInt(el.getAttribute('data-page-index'), 10)
    const submissionSlug = el.getAttribute('data-submission-slug')
    const currentUserId = parseInt(el.getAttribute('data-current-user-id'), 10)
    const commentsLocked = el.getAttribute('data-comments-locked')

    const app = createApp(PdfComments, {
      documentUuid,
      pageIndex,
      submissionSlug,
      currentUserId,
      commentMode: window.commentMode,
      submitters,
      commentsLocked
    })
    app.mount(el)
  })
})

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[new-data-vue-comment]').forEach((el) => {
    const documentUuid = el.getAttribute('data-document-uuid')
    const submitters = el.getAttribute('data-submitters')
    const pageIndex = parseInt(el.getAttribute('data-page-index'), 10)
    const submissionSlug = el.getAttribute('data-submission-slug')
    const currentUserId = parseInt(el.getAttribute('data-current-user-id'), 10)
    const commentsLocked = el.getAttribute('data-comments-locked')

    const app = createApp(NewPdfComments, {
      documentUuid,
      pageIndex,
      submissionSlug,
      currentUserId,
      commentMode: window.commentMode,
      submitters,
      commentsLocked
    })
    app.mount(el)
  })
})

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[new-data-vue-comments-sidebar]').forEach((el) => {
    const submissionSlug = el.getAttribute('data-submission-slug')
    const app = createApp(NewCommentsSidebar, {
      submissionSlug
    })
    app.mount(el)
  })
})

safeRegisterElement(
  'rich-text-editor',
  class extends HTMLElement {
    connectedCallback() {
      if (this.app) return

      this.appElem = document.createElement('div')
      this.app = createApp(RichTextEditor, {
        inputName: this.getAttribute('input-name') || 'faq[answer]',
        initialContent: this.getAttribute('initial-content') || '',
        placeholder: this.getAttribute('placeholder') || 'Write your answer here...'
      })

      this.app.mount(this.appElem)
      this.appendChild(this.appElem)
      this.setAttribute('data-initialized', 'true')
    }

    disconnectedCallback() {
      if (this.app) {
        this.app.unmount()
        this.app = null
      }
      if (this.appElem) {
        this.appElem.remove()
        this.appElem = null
      }
      this.removeAttribute('data-initialized')
    }
  }
)

// Initialize Stimulus
window.Stimulus = Application.start()
const context = require.context('./controllers', true, /\.js$/)
// eslint-disable-next-line no-undef
Stimulus.load(definitionsFromContext(context))
